<div class="bk-page mc">
  <header class="bk-page__head">
    <div class="bk-page__intro">
      <button type="button" class="bk-more mc__back" (click)="goToContactsPage()">
        <span class="bk-sym" aria-hidden="true">arrow_back</span>
        Contacts
      </button>
      <p class="bk-eyebrow">Audience</p>
      <h1 class="bk-page__title">Add several contacts</h1>
      <p class="bk-page__sub">One row per person. A first name and a phone number are required; open More details for the rest.</p>
    </div>
  </header>

  <feedback-card feedBackType="error" [message]="errorMsg" />

  <!-- The result of a save. Always present, so a new result is read out. -->
  <div role="status">
    @if (resultMsg) {
      <p class="mc__result" [class.is-none]="!resultSaved">
        <span class="bk-sym" aria-hidden="true">{{ resultSaved ? 'check_circle' : 'info' }}</span>
        {{ resultMsg }}
      </p>
    }
  </div>

  <form class="mc__form" [formGroup]="allContacts" (ngSubmit)="handleSubmit()" novalidate>
    <ol class="mc__rows" formArrayName="contacts">
      @for (contact of addedContacts.controls; track $index) {
        <li class="bk-panel mc__row" [formGroupName]="$index">
          <div class="mc__row-head">
            <p class="mc__row-title">
              <span class="mc__num">{{ $index + 1 }}</span>
              <span class="mc__name" [class.is-empty]="!rowTitle(contact)">{{ rowTitle(contact) || 'New contact' }}</span>
            </p>
            @if (rowCount > 1) {
              <button
                type="button"
                class="bk-icon-btn"
                [attr.aria-label]="'Remove row ' + ($index + 1)"
                (click)="removeContact($index)"
              >
                <span class="bk-sym" aria-hidden="true">close</span>
              </button>
            }
          </div>

          <div class="mc__grid">
            <input-field
              label="First name"
              [errorMessage]="submitted && contact.get('firstName')?.status == 'INVALID' ? 'Enter a first name' : ''"
            >
              <input class="input-style" formControlName="firstName" autocomplete="off" />
            </input-field>

            <input-field label="Last name">
              <input class="input-style" formControlName="lastName" autocomplete="off" />
            </input-field>

            <input-field
              label="Phone number"
              [errorMessage]="submitted ? phoneErrorMsg(contact.get('primaryContactNumber')) : ''"
            >
              <ngx-mat-input-tel
                formControlName="primaryContactNumber"
                [enablePlaceholder]="true"
                [preferredCountries]="['bw', 'za']"
                [enableSearch]="true"
                [resetOnChange]="true"
                (countryChanged)="contact.patchValue({ zipCode: $event.dialCode })"
              />
            </input-field>

            <input-field
              label="Email"
              [errorMessage]="submitted ? emailErrorMsg(contact.get('emailAddress')) : ''"
            >
              <input class="input-style" type="email" formControlName="emailAddress" autocomplete="off" />
            </input-field>
          </div>

          <button
            type="button"
            class="mc__more"
            [attr.aria-expanded]="!!expanded[$index]"
            [attr.aria-controls]="'mc-more-' + $index"
            (click)="toggleMore($index)"
          >
            <span class="bk-sym mc__more-caret" aria-hidden="true">expand_more</span>
            {{ expanded[$index] ? 'Fewer details' : 'More details' }}
            @if (!expanded[$index] && ((selectedTags[$index] || []).length || (selectedOrganizations[$index] || []).length)) {
              <span class="mc__more-hint">{{ moreHint($index) }}</span>
            }
          </button>

          <div class="mc__extra" [id]="'mc-more-' + $index" [hidden]="!expanded[$index]">
            <div class="mc__grid mc__grid--4">
              <input-field label="Gender">
                <select class="input-style" formControlName="gender">
                  <option value="">Not set</option>
                  <option value="MALE">Male</option>
                  <option value="FEMALE">Female</option>
                  <option value="OTHER">Other</option>
                </select>
              </input-field>

              <input-field label="Birth date">
                <input class="input-style" type="date" formControlName="birthDate" />
              </input-field>

              <input-field label="Country">
                <select class="input-style" formControlName="country">
                  <option value="">Not set</option>
                  @for (country of countries; track country) {
                    <option [value]="country">{{ country }}</option>
                  }
                </select>
              </input-field>

              <input-field label="City">
                <input class="input-style" formControlName="city" autocomplete="off" />
              </input-field>
            </div>

            <div class="mc__grid">
              <group-selection-input
                label="Organizations"
                (openSelectionEvent)="openAddOrganizationModal($index)"
                [selections]="selectedOrganizations[$index] || []"
                (removeSelectionEvent)="removeSelectedOrganization($event, $index)"
                (click)="updateSelectedTagIndex($index)"
              />
              <group-selection-input
                label="Tags"
                (openSelectionEvent)="openAddTagModal($index)"
                [selections]="selectedTags[$index] || []"
                (removeSelectionEvent)="removeSelectedTag($event, $index)"
                (click)="updateSelectedTagIndex($index)"
              />
            </div>
          </div>
        </li>
      }
    </ol>

    <button type="button" class="bk-btn bk-btn--ghost mc__add" (click)="addContact()">
      <span class="bk-sym" aria-hidden="true">add</span>
      Add another contact
    </button>

    <!-- Sticky footer: how many rows, then cancel or save. -->
    <div class="mc__foot">
      <p class="mc__ready">{{ rowCount === 1 ? '1 contact' : rowCount + ' contacts' }} in this list</p>
      <div class="mc__foot-actions">
        <button type="button" class="bk-btn bk-btn--ghost" (click)="goToContactsPage()">Cancel</button>
        <button type="submit" class="bk-btn bk-btn--primary" [disabled]="contactService.isLoading">
          @if (contactService.isLoading) {
            Saving…
          } @else {
            {{ submitLabel }}
          }
        </button>
      </div>
    </div>
  </form>
</div>

<!-- MODALS -->
<assign-tags-modal
  [open]="showAddTagModal"
  (close)="closeAddTagModal()"
  [selection]="selectedTags[selectedContactIdx]"
  (updateSelection)="updateSelectedTags($event)"
/>

<assign-organizations-modal
  [open]="showAddOrganizationModal"
  (updateSelection)="updateSelectedOrganizations($event)"
  (close)="closeAddOrganizationModal()"
  [selection]="selectedOrganizations[selectedContactIdx]"
/>
